/**
 * Site header: brand, quiet nav, language, and a single primary CTA.
 */

import React from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { APP_HOME, REWRITE, REGISTER_TO } from '../utils/paths';
import './Navbar.css';

const Navbar = () => {
  const { logout, isAuthenticated, isPro } = useAuth();
  const { lang, setLang, t, localePath } = useLanguage();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const onApp = pathname.startsWith('/dashboard');

  const handleLogout = () => {
    logout();
    navigate(localePath('/'));
  };

  return (
    <header className="site-nav">
      <div className="container site-nav__inner">
        <Link to={localePath('/')} className="site-nav__brand" aria-label="ClarCV">
          Clar<span>CV</span>
        </Link>

        <nav className="site-nav__links" aria-label={t('nav.mainNav')}>
          <NavLink
            to={localePath('/pricing')}
            className={({ isActive }) =>
              `site-nav__link${isActive ? ' is-active' : ''}`
            }
          >
            {t('nav.pricing')}
          </NavLink>
          <NavLink
            to={localePath('/blog')}
            className={({ isActive }) =>
              `site-nav__link${isActive ? ' is-active' : ''}`
            }
          >
            {t('nav.blog')}
          </NavLink>
          {isAuthenticated ? (
            <NavLink
              to="/dashboard/settings"
              className={({ isActive }) =>
                `site-nav__link site-nav__link--account${isActive ? ' is-active' : ''}`
              }
            >
              {t('nav.account')}
            </NavLink>
          ) : null}
        </nav>

        <div className="site-nav__end">
          <div className="site-nav__lang" role="group" aria-label={t('nav.langLabel')}>
            <button
              type="button"
              className={`site-nav__lang-btn${lang === 'es' ? ' is-active' : ''}`}
              onClick={() => setLang('es')}
              aria-pressed={lang === 'es'}
            >
              {t('nav.langEs')}
            </button>
            <button
              type="button"
              className={`site-nav__lang-btn${lang === 'en' ? ' is-active' : ''}`}
              onClick={() => setLang('en')}
              aria-pressed={lang === 'en'}
            >
              {t('nav.langEn')}
            </button>
          </div>

          {isAuthenticated ? (
            <div className="site-nav__account">
              <div className="site-nav__identity">
                <span
                  className={`site-nav__plan${isPro ? ' is-pro' : ''}`}
                  title={isPro ? t('nav.planPro') : t('nav.planFree')}
                >
                  {isPro ? t('nav.planPro') : t('nav.planFree')}
                </span>
              </div>
              <Link
                to={APP_HOME}
                className={`site-nav__cta site-nav__cta--app${onApp ? ' is-current' : ''}`}
                aria-current={onApp ? 'page' : undefined}
              >
                {t('nav.dashboard')}
              </Link>
              <button type="button" onClick={handleLogout} className="site-nav__text-action">
                {t('nav.logout')}
              </button>
            </div>
          ) : pathname.startsWith('/login') ? null : (
            <div className="site-nav__guest">
              <Link to="/login" className="site-nav__text-action site-nav__text-action--login">
                {t('nav.login')}
              </Link>
              {onApp ? (
                <Link to={REGISTER_TO} className="site-nav__cta site-nav__cta--auth">
                  {t('nav.signup')}
                </Link>
              ) : (
                <Link to={REWRITE} className="site-nav__cta site-nav__cta--auth">
                  {t('nav.tryApp')}
                </Link>
              )}
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Navbar;
